/**
 * repositories/userSyncDevicesRepository.ts
 * Repository for user_sync_devices table operations
 *
 * Handles device registration and sync tracking for multi-device workflows.
 * Enforces composite key security: all writes require (userId, deviceId).
 *
 * SECURITY NOTE: This table is security-adjacent (tracks "what clients claim to be me").
 * All mutating operations validate BOTH userId AND deviceId to prevent cross-user mutations.
 */

import { supabase } from '@/lib/supabase';
import { toDomain, toWire, toPatch } from '@/lib/supabaseMappers';
import type { Database } from '@/types/supabase.types';
import type {
  UserSyncDevice,
  UserSyncDeviceInsert,
  UserSyncDeviceUpdate
} from '@/types/supabase/UserSyncDevice';

// Wire type from generated Supabase types
type UserSyncDeviceRow = Database['public']['Tables']['user_sync_devices']['Row'];

// NOTE: If you add/remove columns in user_sync_devices,
// update DEVICE_PROJECTION AND UserSyncDevice in src/types/supabase/UserSyncDevice.ts.
const DEVICE_PROJECTION =
  'id,user_id,device_id,device_name,sync_provider,last_sync_at,last_exported_at,last_imported_at,directory_handle_stored,created_at,updated_at' as const;

/**
 * User Sync Devices Repository
 *
 * Provides data access layer for device registration and sync tracking.
 * All writes require (userId, deviceId) composite key to prevent cross-user mutations.
 */
export const userSyncDevicesRepository = {
  /**
   * Get all devices for a user
   *
   * @param userId - User ID to fetch devices for
   * @returns Promise resolving to array of user's devices (ordered by last sync, most recent first)
   */
  async getByUserId(userId: string): Promise<UserSyncDevice[]> {
    const { data, error } = await supabase
      .from('user_sync_devices')
      .select(DEVICE_PROJECTION)
      .eq('user_id', userId)
      .order('last_sync_at', { ascending: false, nullsFirst: false });

    if (error) {
      throw new Error(`Failed to get devices for user: ${error.message}`);
    }

    return (data || []).map((row) => toDomain<UserSyncDevice>(row as UserSyncDeviceRow));
  },

  /**
   * Get device by composite key (userId + deviceId)
   *
   * @param userId - User ID who owns the device
   * @param deviceId - Device ID to lookup
   * @returns Promise resolving to device or null if not found
   */
  async getByDeviceId(
    userId: string,
    deviceId: string
  ): Promise<UserSyncDevice | null> {
    const { data, error } = await supabase
      .from('user_sync_devices')
      .select(DEVICE_PROJECTION)
      .eq('user_id', userId)
      .eq('device_id', deviceId)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to get device: ${error.message}`);
    }

    if (!data) return null;

    return toDomain<UserSyncDevice>(data as UserSyncDeviceRow);
  },

  /**
   * Create new device record
   *
   * @param deviceData - Device data to insert
   * @returns Promise resolving to created device
   */
  async create(deviceData: UserSyncDeviceInsert): Promise<UserSyncDevice> {
    const wireData = toWire<UserSyncDeviceRow>(deviceData);

    const { data, error } = await supabase
      .from('user_sync_devices')
      .insert(wireData)
      .select(DEVICE_PROJECTION)
      .single();

    if (error) {
      throw new Error(`Failed to create device: ${error.message}`);
    }

    return toDomain<UserSyncDevice>(data as UserSyncDeviceRow);
  },

  /**
   * Update device by composite key (userId + deviceId)
   *
   * SECURITY: Requires both userId + deviceId to prevent cross-user mutations.
   * WHERE clause: user_id = $userId AND device_id = $deviceId
   *
   * Only updates mutable lifecycle fields (deviceName, syncProvider, lastSyncAt, directoryHandleStored).
   * Does NOT allow changing userId or deviceId; those identify the device.
   *
   * @param userId - User ID who owns the device
   * @param deviceId - Device ID to update
   * @param updates - Partial updates to apply
   * @returns Promise resolving to updated device
   */
  async update(
    userId: string,
    deviceId: string,
    updates: UserSyncDeviceUpdate
  ): Promise<UserSyncDevice> {
    const wireUpdates = toPatch<UserSyncDeviceRow>(updates);

    const { data, error } = await supabase
      .from('user_sync_devices')
      .update(wireUpdates)
      .eq('user_id', userId)
      .eq('device_id', deviceId)
      .select(DEVICE_PROJECTION)
      .single();

    if (error) {
      throw new Error(`Failed to update device: ${error.message}`);
    }

    return toDomain<UserSyncDevice>(data as UserSyncDeviceRow);
  },

  /**
   * Delete device by composite key (userId + deviceId)
   *
   * SECURITY: Requires both userId + deviceId to prevent cross-user deletions.
   * WHERE clause: user_id = $userId AND device_id = $deviceId
   *
   * Permanently removes this device record for this user.
   * This is intended for 'disconnect this device' UI, not automatic cleanup.
   * Callers must ensure they're acting on the current user's own device list.
   *
   * @param userId - User ID who owns the device
   * @param deviceId - Device ID to delete
   */
  async delete(userId: string, deviceId: string): Promise<void> {
    const { error } = await supabase
      .from('user_sync_devices')
      .delete()
      .eq('user_id', userId)
      .eq('device_id', deviceId);

    if (error) {
      throw new Error(`Failed to delete device: ${error.message}`);
    }
  },

  /**
   * Update last sync timestamp for device
   *
   * SECURITY: Requires both userId + deviceId.
   * Specialized update for last_sync_at only (performance optimization).
   *
   * Updates only lastSyncAt. Does not return the updated row.
   * Call getByDeviceId(userId, deviceId) if you need the latest state.
   *
   * @param userId - User ID who owns the device
   * @param deviceId - Device ID to update
   * @param timestampIso - ISO 8601 timestamp (from new Date().toISOString())
   */
  async updateLastSync(
    userId: string,
    deviceId: string,
    timestampIso: string
  ): Promise<void> {
    const { error } = await supabase
      .from('user_sync_devices')
      .update({ last_sync_at: timestampIso })
      .eq('user_id', userId)
      .eq('device_id', deviceId);

    if (error) {
      throw new Error(`Failed to update last sync: ${error.message}`);
    }
  },

  /**
   * Update export/import timestamps for device
   *
   * SECURITY: Requires both userId + deviceId.
   * Combined update for efficiency (single DB call when both timestamps update).
   *
   * Only updates provided timestamps; omitted fields are not touched.
   * Does not return the updated row for performance.
   *
   * @param userId - User ID who owns the device
   * @param deviceId - Device ID to update
   * @param timestamps - Object with optional exportedAt and/or importedAt ISO timestamps
   */
  async updateSyncTimestamps(
    userId: string,
    deviceId: string,
    timestamps: { exportedAt?: string; importedAt?: string }
  ): Promise<void> {
    // Build update object with only provided fields
    const updates: Record<string, string> = {};
    if (timestamps.exportedAt !== undefined) {
      updates['last_exported_at'] = timestamps.exportedAt;
    }
    if (timestamps.importedAt !== undefined) {
      updates['last_imported_at'] = timestamps.importedAt;
    }

    // Guard: don't make DB call if nothing to update
    if (Object.keys(updates).length === 0) {
      return;
    }

    const { error } = await supabase
      .from('user_sync_devices')
      .update(updates)
      .eq('user_id', userId)
      .eq('device_id', deviceId);

    if (error) {
      throw new Error(`Failed to update sync timestamps: ${error.message}`);
    }
  },

  /**
   * Update directory handle stored flag
   *
   * SECURITY: Requires both userId + deviceId.
   * Specialized update for directory_handle_stored only (performance optimization).
   *
   * Updates only directoryHandleStored. Does not return the updated row.
   * Call getByDeviceId(userId, deviceId) if you need the latest state.
   *
   * @param userId - User ID who owns the device
   * @param deviceId - Device ID to update
   * @param stored - Whether directory handle is stored in IndexedDB
   */
  async updateDirectoryHandleStored(
    userId: string,
    deviceId: string,
    stored: boolean
  ): Promise<void> {
    const { error } = await supabase
      .from('user_sync_devices')
      .update({ directory_handle_stored: stored })
      .eq('user_id', userId)
      .eq('device_id', deviceId);

    if (error) {
      throw new Error(`Failed to update directory handle stored: ${error.message}`);
    }
  },

  /**
   * Get device by database ID (internal PK lookup)
   *
   * NOTE: This is only used in DeviceService for internal lifecycle checks.
   * Prefer getByDeviceId() for normal lookups.
   *
   * Looks up device by the database primary key (id column), not the logical deviceId.
   * Returns minimal info needed to then call other scoped methods.
   *
   * @param id - Database primary key (UUID)
   * @returns Promise resolving to device or null if not found
   *
   * @example
   * // Internal lifecycle lookup by DB PK
   * const device = await userSyncDevicesRepository.getByDbId(dbId);
   * if (device) {
   *   await userSyncDevicesRepository.delete(device.userId, device.deviceId);
   * }
   */
  async getByDbId(id: string): Promise<UserSyncDevice | null> {
    const { data, error } = await supabase
      .from('user_sync_devices')
      .select(DEVICE_PROJECTION)
      .eq('id', id)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to get device by ID: ${error.message}`);
    }

    if (!data) return null;

    return toDomain<UserSyncDevice>(data as UserSyncDeviceRow);
  }
};
